/**
 * Activation role keys. Mirrors backend/utils/roles.js — keep the two in step.
 *
 * The five team roles own and approve steps in the Activations module. Their
 * reach is limited in TWO different places, and the difference matters:
 *
 *  - Here and in App.js / Sidebar.js: which screens they can open. They reach
 *    Marketing > Activation and nothing else.
 *  - In backend/middleware/auth.js: which API calls they can make. They read at
 *    'user' level wherever a route admits 'user' through authorize(), because
 *    their own screens look up brands, doors, products and marketing categories;
 *    do not lock them out of those endpoints. Routes gated with authorizeExact()
 *    refuse them: the sales, purchase, budget, price, stock and planning modules
 *    (SALES_DATA_ROLES / STAFF_ROLES below), the Beauty Advisor Sales reads and
 *    the Arrival Pipeline.
 *
 * So `hasRole` carries no alias for them: a team role passes a route's
 * requiredRoles only when that route names it.
 */

/** Teams that own or approve activation workflow steps. */
export const ACTIVATION_TEAM_ROLES = [
  'vm',
  'local_brand',
  'social_media',
  'kol_manager',
  'ecommerce',
];

/** Every role the Activations module is visible to. */
export const ACTIVATION_ROLES = [
  'admin',
  'manager',
  'brand_manager',
  ...ACTIVATION_TEAM_ROLES,
];

/**
 * Roles that see an activation's linked expenses and spend, and the only roles
 * that create activations. Mirrors ACTIVATION_SPEND_ROLES in backend/utils/roles.js.
 */
export const ACTIVATION_SPEND_ROLES = ['admin', 'manager', 'brand_manager'];

/**
 * Roles that read sales figures (DoorSales, ProductSale and anything built from them)
 * org-wide. Mirrors SALES_DATA_ROLES in backend/utils/roles.js; page guards and
 * sidebar entries for those pages name these roles, with brand_manager added where
 * its data is scoped.
 */
export const SALES_DATA_ROLES = ['admin', 'manager', 'reporting_manager'];

/**
 * SALES_DATA_ROLES plus plain `user`. Mirrors STAFF_ROLES in backend/utils/roles.js.
 */
export const STAFF_ROLES = ['admin', 'manager', 'user', 'reporting_manager'];

/**
 * Roles that open the Novelty Pipeline. Mirrors NOVELTY_PIPELINE_ROLES in
 * backend/utils/roles.js.
 */
export const NOVELTY_PIPELINE_ROLES = ['admin', 'manager', 'brand_manager'];

/**
 * Roles that approve a Sales Report run for sending. Mirrors SALES_REPORT_APPROVE_ROLES
 * in backend/utils/roles.js.
 */
export const SALES_REPORT_APPROVE_ROLES = ['admin', 'manager'];

/**
 * Roles that create and edit suppliers, the fixed BDT rate included. A budget save
 * changes its supplier's fixed rate only for these roles. Mirrors
 * SUPPLIER_WRITE_ROLES in backend/utils/roles.js.
 */
export const SUPPLIER_WRITE_ROLES = ['admin', 'manager', 'reporting_manager'];

/**
 * Roles scoped to their assigned suppliers. Mirrors SUPPLIER_SCOPED_ROLES in
 * backend/utils/roles.js — these are the roles the user admin offers a supplier
 * picker for, and the only ones whose `suppliers` list means anything.
 */
export const SUPPLIER_SCOPED_ROLES = ['brand_manager', 'local_brand'];

/**
 * Every assignable role, in the order the user admin lists them: the general
 * roles first, then the activation teams.
 */
export const ASSIGNABLE_ROLES = [
  'user',
  'brand_manager',
  'reporting_manager',
  'manager',
  'admin',
  ...ACTIVATION_TEAM_ROLES,
];

/** Team roles whose only screen is Marketing > Activation. */
export const ACTIVATION_ONLY_ROLES = ACTIVATION_TEAM_ROLES;

/** Display labels for the team roles, used by the user admin and step pills. */
export const ROLE_LABELS = {
  admin: 'Admin',
  manager: 'Manager',
  brand_manager: 'Brand Manager',
  reporting_manager: 'Reporting Manager',
  user: 'User',
  vm: 'VM Team',
  local_brand: 'Local Brand Team',
  social_media: 'Social Media Team',
  kol_manager: 'KOL Manager',
  ecommerce: 'E-commerce Team',
};

/**
 * The activation team each role acts as inside the workflow. Global Brand is
 * `brand_manager` and Management is `manager` — the module reuses the existing
 * roles rather than duplicating them.
 */
export const ACTIVATION_TEAM_LABELS = {
  admin: 'Admin',
  manager: 'Management',
  brand_manager: 'Global Brand Team',
  vm: 'VM Team',
  local_brand: 'Local Brand Team',
  social_media: 'Social Media Team',
  kol_manager: 'KOL Manager',
  ecommerce: 'E-commerce Team',
};
